import { Field, FieldSet } from "../modelDialogs/BaseDialog";
import type Device from "../models/Device";
import type DeviceTemplate from "../models/DeviceTemplate";
import { itx } from "../translationItx/itx";
import { isUiModeProductAdmin, uiModeWatcher } from "../uiMode";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import DeviceBasePropertyInput from "./DeviceBasePropertyInput";
import type { BaseDeviceProps } from "../models/BaseDeviceProps";
import ProductDataSection from "./ProductDataSection";
import UI_KnxProductData from "./UI_KnxProductData";

/** Product information and effective base properties, editable by product administrators. */
export default function DeviceProductDetails({ device, template = device.template }: { device: Device; template?: DeviceTemplate }) {
  useOnChangeWatcher(device);
  useOnChangeWatcher(template);
  useOnChangeWatcher(uiModeWatcher);
  const editable = isUiModeProductAdmin() && !!template;
  const updateProperty = (group: keyof BaseDeviceProps, key: string, value: string | number | boolean) => {
    if (!isUiModeProductAdmin() || !template) return;
    const current = template.baseProps ?? device.baseProps;
    template.baseProps = {...current, [group]: {...current[group], [key]: value}};
    state.undoManager.userInteractionEnded();
  };
  const labels: Record<string, string> = {
    type: itx("baseProps.type", "Typ"),
    horizontal: itx("baseProps.orientation", "Ausrichtung"),
    numOfPages: itx("baseProps.numOfPages", "Seiten"),
    numberOfButtonRows: itx("baseProps.keys", "Tasten"),
    supportsSplitRocker: itx("baseProps.divisible", "Teilbar"),
    supportsSlap: itx("baseProps.slapSupport", "Patsch Unterstützung"),
    supportsLongPress: itx("baseProps.supportsLongPress", "Langer Tastendruck"),
    statusLeds: itx("baseProps.statusLeds", "Status-LEDs"),
    hasDisplay: itx("baseProps.hasDisplay", "Display"),
    color: itx("baseProps.color", "Farbe"),
    heightMm: itx("baseProps.heightMm", "Höhe (mm)"),
    widthMm: itx("baseProps.widthMm", "Breite (mm)"),
    channels: itx("baseProps.channels", "Kanäle"),
    deviceType: itx("baseProps.deviceType", "Gerätetyp"),
  };
  const formatValue = (key: string, value: unknown): string => {
    if (key === "horizontal") return value ? itx("baseProps.horizontalValue", "Horizontal") : itx("baseProps.verticalValue", "Vertikal");
    if (key === "supportsSlap") {
      if (value === 2) return itx("baseProps.longSlapSupported", "Langer Patsch unterstützt");
      return value === 1 ? itx("baseProps.yes", "Ja") : itx("baseProps.no", "Nein");
    }
    if (key === "supportsLongPress") {
      if (value === 2) return itx("baseProps.extraLongSupported", "Extralang unterstützt");
      return value === 1 ? itx("baseProps.yes", "Ja") : itx("baseProps.no", "Nein");
    }
    return typeof value === "boolean" ? (value ? itx("baseProps.yes", "Ja") : itx("baseProps.no", "Nein")) : String(value);
  };
  const propertyOrder = Object.keys(labels);
  const rank = (key: string) => propertyOrder.includes(key) ? propertyOrder.indexOf(key) : propertyOrder.length;
  const rows = Object.entries(device.baseProps).flatMap(([group, properties]) =>
    Object.entries(properties ?? {}).filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => rank(left) - rank(right)).map(([key, value]) => ({
      id: `${group}.${key}`, group: group as keyof BaseDeviceProps, property: key, rawValue: value as string | number | boolean, label: labels[key] ?? key,
      value: formatValue(key, value),
    })),
  );

  return <>
    {template?.isGeneric ? (
      <ProductDataSection title={itx("ui_KnxProductData.productData", "Produktdaten")}>
        <FieldSet style={{ userSelect: "text" }}>
          <Field label={itx("ui_KnxProductData.productName", "Produktbezeichnung")}>{template.name}</Field>
        </FieldSet>
      </ProductDataSection>
    ) : template?.knxProductData ? (
      <UI_KnxProductData key={template.knxProductData.id} productData={template.knxProductData} orderInformation={template.orderInformation} device={device} />
    ) : null}
    <ProductDataSection title={itx("baseProps.title", "Basiseigenschaften")}>
      <FieldSet style={{ userSelect: "text" }}>
        {rows.length ? rows.map(row => <Field key={row.id} label={row.label}>{editable
          ? <DeviceBasePropertyInput property={row.property} value={row.rawValue} label={row.label} onChange={value => updateProperty(row.group, row.property, value)} />
          : row.value}</Field>) :
          <Field>{itx("baseProps.empty", "Keine Basiseigenschaften vorhanden")}</Field>}
      </FieldSet>
      {editable && template?.baseProps && <button type="button" onClick={() => {
        if (!isUiModeProductAdmin() || !template) return;
        template.baseProps = undefined;
        state.undoManager.userInteractionEnded();
      }}>{itx("baseProps.resetTemplateProperties", "Basiseigenschaften zurücksetzen")}</button>}
    </ProductDataSection>
  </>;
}
